import { useMemo, useCallback, useState } from "react";
import { Checkbox, Typography, Box } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Button, CircularProgress } from "@mui/material";

import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import ImagePlaceholder from "components/Image/ImagePlaceholder";
import ModalComponent from "./modal";

import { columnTextField } from "utils/tables-utils";

import { SELECT_ALL_PRODUCTS_MAX } from "../../utils/wizard-helpers";

export default function SelectProductsTable({
  data,
  loading,
  totalSize,
  currentPage,
  currentSizePerPage,
  selectedProducts,
  setSelectedProducts,
  handlePaginationChange,
  onSelectAllResults,
}) {
  const [openSelectAllDialog, setOpenSelectAllDialog] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const handleSelectProduct = useCallback(
    (product, selectAll = false) => {
      setSelectedProducts((prev) => {
        if (selectAll) {
          const allSelected =
            data.length > 0 &&
            data.every((item) => prev.some((p) => p.Codigo === item.Codigo));

          if (allSelected) {
            return prev.filter(
              (p) => !data.some((item) => item.Codigo === p.Codigo),
            );
          } else {
            const newSelections = data.filter(
              (item) => !prev.some((p) => p.Codigo === item.Codigo),
            );
            return [...prev, ...newSelections];
          }
        }

        const exists = prev.some((p) => p.Codigo === product.Codigo);

        return exists
          ? prev.filter((p) => p.Codigo !== product.Codigo)
          : [...prev, product];
      });
    },
    [data, setSelectedProducts],
  );

  const tableData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      selected: selectedProducts.some((p) => p.Codigo === item.Codigo),
    }));
  }, [data, selectedProducts]);

  const handleHeaderSelectClick = useCallback(() => {
    const allSelectedInPage =
      tableData.length > 0 && tableData.every((item) => item.selected);

    if (allSelectedInPage) {
      handleSelectProduct(null, true);
      return;
    }

    setOpenSelectAllDialog(true);
  }, [handleSelectProduct, tableData]);

  const columnModel = useMemo(
    () => [
      columnTextField({
        dataField: "Codigo",
        text: "Cód. Produto",
        align: "center",
      }),
      columnTextField({
        dataField: "nome",
        text: "Nome Comercial",
        align: "left",
        formatter: (_, row) => row?.nome ?? row?.Nome ?? "",
      }),
      columnTextField({
        dataField: "LinhaProduto",
        text: "Linha",
        align: "center",
      }),
      columnTextField({
        dataField: "Marca",
        text: "Marca",
        align: "center",
      }),
      columnTextField({
        dataField: "UnidadeMedida",
        text: "Unid. Medida",
        align: "center",
      }),
      columnTextField({
        dataField: "TamanhoMedidas",
        text: "Tamanho",
        align: "center",
      }),
      columnTextField({
        dataField: "QuantidadeDisponivel",
        text: "Estoque",
        align: "center",
      }),
      columnTextField({
        dataField: "FotoPrincipal",
        text: "Imagem",
        align: "center",
        formatter: (_, row) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <ImagePlaceholder
              src={row?.FotoPrincipal ?? row?.foto}
              alt={`Imagem do produto ${row?.Codigo}`}
              width="40px"
              height="40px"
              widthOnError="40px"
              heightOnError="40px"
            />
          </div>
        ),
      }),
      {
        dataField: "selected",
        text: "",
        align: "center",
        headerAlign: "center",
        formatter: (_, row) => (
          <Checkbox
            checked={row.selected}
            onChange={() => handleSelectProduct(row)}
          />
        ),
        headerFormatter: () => (
          <Checkbox
            checked={
              tableData.length > 0 && tableData.every((item) => item.selected)
            }
            indeterminate={
              tableData.some((item) => item.selected) &&
              !tableData.every((item) => item.selected)
            }
            onChange={handleHeaderSelectClick}
          />
        ),
      },
    ],
    [handleHeaderSelectClick, handleSelectProduct, tableData],
  );

  const handleSelectAllCurrentPage = useCallback(() => {
    handleSelectProduct(null, true);
    setOpenSelectAllDialog(false);
  }, [handleSelectProduct]);

  const handleSelectAllTable = useCallback(async () => {
    if (!onSelectAllResults) {
      handleSelectAllCurrentPage();
      return;
    }

    setSelectAllLoading(true);
    try {
      const all = (await onSelectAllResults()) ?? [];
      setSelectedProducts((prev) => {
        const newOnes = all.filter(
          (item) => !prev.some((p) => p.Codigo === item.Codigo),
        );
        return [...prev, ...newOnes];
      });
      setOpenSelectAllDialog(false);
    } finally {
      setSelectAllLoading(false);
    }
  }, [handleSelectAllCurrentPage, onSelectAllResults, setSelectedProducts]);

  return (
    <div>
      {loading ? (
        <Box p={2}>
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
        </Box>
      ) : (
        <>
          <Box
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="textSecondary">
              {selectedProducts.length > 0
                ? `${selectedProducts.length} produto(s) selecionado(s)`
                : "Nenhum produto selecionado"}
            </Typography>
          </Box>
          <Box
            sx={{
              minHeight: 360,
              "& .table-responsive": {
                overflowX: "auto",
              },
              "& table": {
                minWidth: 960,
              },
              "& th": {
                whiteSpace: "nowrap",
                fontSize: "0.8rem",
              },
              "& td": {
                fontSize: "0.85rem",
                verticalAlign: "middle",
              },
            }}
          >
            <ToolkitTable
              keyField="Codigo"
              dataTable={tableData}
              columnModel={columnModel}
              hideEmptyMessages
              bootstrapTableProps={{
                remote: true,
                onTableChange: (type, { page, sizePerPage }) => {
                  if (type === "pagination") {
                    handlePaginationChange(page, sizePerPage);
                  }
                },
              }}
              pagination={{
                options: [5, 10, 25, 50, 100],
                totalSize,
                page: currentPage,
                sizePerPage: currentSizePerPage,
                defaultOption: currentSizePerPage,
                showTotal: true,
              }}
            />
          </Box>
          <ModalComponent
            open={openSelectAllDialog}
            onClose={() =>
              selectAllLoading ? null : setOpenSelectAllDialog(false)
            }
            width="520px"
          >
            <Typography variant="h2" style={{ marginBottom: 12 }}>
              Selecionar produtos
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Selecione a abrangência:
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              - Página atual: {data.length} itens
              <br />- Todos os resultados: {totalSize} itens
              {totalSize > SELECT_ALL_PRODUCTS_MAX ? (
                <>
                  <br />
                  (máximo {SELECT_ALL_PRODUCTS_MAX} por seleção em massa)
                </>
              ) : null}
            </Typography>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              style={{ gap: 12, marginTop: 20 }}
            >
              <Button
                variant="outlined"
                disabled={selectAllLoading}
                onClick={() => setOpenSelectAllDialog(false)}
                style={{ padding: 10 }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                disabled={selectAllLoading}
                onClick={handleSelectAllCurrentPage}
                style={{ padding: 10 }}
              >
                Somente página
              </Button>
              <Button
                variant="contained"
                disabled={selectAllLoading}
                onClick={handleSelectAllTable}
                style={{ padding: 10 }}
              >
                {selectAllLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Todos resultados"
                )}
              </Button>
            </Box>
          </ModalComponent>
        </>
      )}
    </div>
  );
}
